<template>
    <div>
        <!-- ① Request Details -->
        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-4">
            <span class="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold tw-flex-shrink-0">1</span>
            <span class="tw-text-sm tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">{{ $t("cashRequest.requestInfo") }}</span>
            <div class="tw-flex-1 tw-border-t tw-border-gray-200"></div>
        </div>

        <div class="row">
            <!-- Requester chip + Date in one row -->
            <div class="col-md-6 tw-mb-3">
                <label class="form-col-label control-label">{{ $t("cashRequest.requestedBy") }}</label>
                <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-h-9 tw-rounded tw-border tw-border-gray-200 tw-bg-gray-50">
                    <span class="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-bg-blue-500 tw-text-white tw-text-xs tw-font-semibold tw-flex-shrink-0">
                        {{ nameInitial }}
                    </span>
                    <span class="tw-text-sm tw-text-gray-700 tw-truncate">{{ requestedByName }}</span>
                </div>
            </div>

            <div class="col-md-6 tw-mb-3">
                <label class="required form-col-label control-label">{{ $t("cashRequest.requestDate") }}</label>
                <DatePicker
                    :value="model.request_date"
                    type="date"
                    format="dd-MM-yyyy"
                    :placeholder="$t('cashRequest.requestDate')"
                    style="width: 100%"
                    :class="{ 'is-invalid': validate.has('request_date') }"
                    @on-change="model.request_date = $event"
                />
                <div class="invalid-feedback" v-if="validate.has('request_date')">
                    {{ validate.first("request_date") }}
                </div>
            </div>

            <!-- Amount + Currency -->
            <div class="col-md-12 tw-mb-1">
                <label class="required form-col-label control-label">{{ $t("cashRequest.amount") }}</label>
                <Input
                    v-model="model.request_amount"
                    type="number"
                    :min="0"
                    :placeholder="$t('cashRequest.amount')"
                    :class="{ 'is-invalid': validate.has('request_amount') }"
                    size="large"
                >
                    <Select
                        slot="append"
                        v-model="model.currency_id"
                        style="width: 120px"
                    >
                        <Option
                            v-for="cur in currencies"
                            :key="cur.currency_id"
                            :value="cur.currency_id"
                        >
                            {{ cur.currency_code }} — {{ cur.currency_name_en }}
                        </Option>
                    </Select>
                </Input>
                <div class="invalid-feedback" v-if="validate.has('request_amount')">
                    {{ validate.first("request_amount") }}
                </div>
                <div class="invalid-feedback" v-if="validate.has('currency_id')">
                    {{ validate.first("currency_id") }}
                </div>
            </div>

            <!-- Amount preview -->
            <div class="col-md-12 tw-mb-3">
                <transition name="fade">
                    <div
                        v-if="formattedAmount"
                        class="tw-flex tw-items-center tw-gap-1.5 tw-mt-1.5 tw-text-blue-600 tw-text-sm tw-font-medium"
                    >
                        <i class="fas fa-check-circle tw-text-xs"></i>
                        <span>{{ formattedAmount }}</span>
                    </div>
                </transition>
            </div>

            <div class="col-md-12 tw-mb-3">
                <label class="required form-col-label control-label">{{ $t("cashRequest.purpose") }}</label>
                <textarea
                    v-model.trim="model.purpose"
                    rows="3"
                    class="form-control"
                    :class="{ 'is-invalid': validate.has('purpose') }"
                    :placeholder="$t('cashRequest.purpose')"
                ></textarea>
                <div class="invalid-feedback" v-if="validate.has('purpose')">
                    {{ validate.first("purpose") }}
                </div>
            </div>

            <div class="col-md-12 tw-mb-5">
                <label class="form-col-label control-label">{{ $t("cashRequest.remark") }}</label>
                <textarea
                    v-model.trim="model.remark"
                    rows="2"
                    class="form-control"
                    :placeholder="$t('cashRequest.remark')"
                ></textarea>
            </div>
        </div>

        <!-- ② Receiving Bank Account -->
        <div class="tw-flex tw-items-start tw-gap-3 tw-mb-4">
            <span class="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-green-500 tw-text-white tw-text-xs tw-font-bold tw-flex-shrink-0 tw-mt-0.5">2</span>
            <div class="tw-flex-1">
                <div class="tw-flex tw-items-center tw-gap-3">
                    <span class="tw-text-sm tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">{{ $t("cashRequest.receivingBankAccount") }}</span>
                    <div class="tw-flex-1 tw-border-t tw-border-gray-200"></div>
                </div>
                <p class="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0">{{ $t("cashRequest.receivingBankAccountHint") }}</p>
            </div>
        </div>

        <div class="row">
            <div class="col-md-6 tw-mb-3">
                <label class="required form-col-label control-label">{{ $t("cashRequest.bank") }}</label>
                <Select
                    v-model="model.bank_id"
                    :placeholder="$t('cashRequest.bank')"
                    filterable
                    :class="{ 'is-invalid': validate.has('bank_id') }"
                    @on-change="onBankChange"
                >
                    <Option
                        v-for="bank in banks"
                        :key="bank.bank_id"
                        :value="bank.bank_id"
                        :label="bank.bank_name"
                    >
                        <span class="tw-flex tw-items-center tw-gap-2">
                            <span class="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded tw-bg-green-100 tw-text-green-700 tw-text-xs tw-font-bold tw-flex-shrink-0">
                                {{ bank.bank_name.charAt(0).toUpperCase() }}
                            </span>
                            {{ bank.bank_name }}
                        </span>
                    </Option>
                </Select>
                <div class="invalid-feedback" v-if="validate.has('bank_id')">
                    {{ validate.first("bank_id") }}
                </div>
            </div>

            <div class="col-md-6 tw-mb-3">
                <label class="required form-col-label control-label">{{ $t("cashRequest.bankAccount") }}</label>
                <Select
                    v-model="selectedAccountId"
                    :placeholder="model.bank_id ? $t('cashRequest.selectAccount') : $t('cashRequest.selectBankFirst')"
                    filterable
                    :disabled="!model.bank_id || filteredAccounts.length === 0"
                    :class="{ 'is-invalid': validate.has('bank_account_no') }"
                    @on-change="onAccountChange"
                >
                    <Option
                        v-for="acc in filteredAccounts"
                        :key="acc.account_id"
                        :value="acc.account_id"
                        :label="acc.account_no"
                    >
                        <div class="tw-py-0.5">
                            <div class="tw-font-medium tw-text-gray-800 tw-text-sm">{{ acc.account_no }}</div>
                            <div class="tw-text-xs tw-text-gray-400">{{ acc.account_name }}</div>
                        </div>
                    </Option>
                </Select>
                <div class="invalid-feedback" v-if="validate.has('bank_account_no')">
                    {{ validate.first("bank_account_no") }}
                </div>
                <div
                    v-if="model.bank_id && filteredAccounts.length === 0"
                    class="tw-mt-1 tw-text-xs tw-text-amber-600 tw-flex tw-items-center tw-gap-1"
                >
                    <i class="fas fa-exclamation-triangle"></i>
                    {{ $t("cashRequest.noAccountsForBank") }}
                </div>
            </div>

            <!-- Account info card -->
            <div class="col-md-12 tw-mb-2">
                <transition name="slide-down">
                    <div v-if="selectedAccount" class="tw-rounded-lg tw-border tw-border-green-200 tw-bg-green-50 tw-overflow-hidden">
                        <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-bg-green-100 tw-border-b tw-border-green-200">
                            <i class="fas fa-university tw-text-green-600 tw-text-xs"></i>
                            <span class="tw-text-xs tw-font-semibold tw-text-green-700 tw-uppercase tw-tracking-wide">{{ $t("cashRequest.selectedAccount") }}</span>
                            <div class="tw-flex-1"></div>
                            <button type="button" class="tw-text-green-400 hover:tw-text-red-500 tw-transition-colors tw-text-xs" @click="clearAccount" :title="$t('cashRequest.clearAccount')">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <div class="tw-p-3 tw-flex tw-items-center tw-gap-3">
                            <div class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-full tw-bg-green-200 tw-text-green-700 tw-font-bold tw-text-lg tw-flex-shrink-0">
                                {{ selectedBank ? selectedBank.bank_name.charAt(0).toUpperCase() : '?' }}
                            </div>
                            <div class="tw-flex-1">
                                <div class="tw-font-semibold tw-text-gray-800 tw-text-sm">{{ selectedBank ? selectedBank.bank_name : '' }}</div>
                                <div class="tw-flex tw-flex-wrap tw-gap-x-4 tw-mt-1">
                                    <div class="tw-text-xs tw-text-gray-500">
                                        {{ $t("cashRequest.bankAccountNo") }}:
                                        <span class="tw-font-mono tw-font-semibold tw-text-gray-800 tw-ml-1">{{ selectedAccount.account_no }}</span>
                                    </div>
                                    <div class="tw-text-xs tw-text-gray-500">
                                        {{ $t("cashRequest.bankAccountName") }}:
                                        <span class="tw-font-semibold tw-text-gray-800 tw-ml-1">{{ selectedAccount.account_name }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="model.bank_id && filteredAccounts.length > 0" class="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-text-sm tw-text-gray-400">
                        <i class="fas fa-arrow-up tw-text-xs"></i>
                        <span>{{ $t("cashRequest.selectAccountHint") }}</span>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CashRequestFormInput",
    props: {
        value: { type: Object, required: true },
        validate: { type: Object, required: true },
        requestedByName: { type: String, default: "" },
        currencies: { type: Array, default: () => [] },
        banks: { type: Array, default: () => [] }
    },
    data() {
        return {
            selectedAccountId: null
        };
    },
    watch: {
        banks: {
            immediate: true,
            handler(banks) {
                if (!banks || !banks.length) return;
                // Auto-select bank if only one
                if (!this.model.bank_id && banks.length === 1) {
                    this.model.bank_id = banks[0].bank_id;
                }
                // Restore selectedAccountId from saved bank_account_no
                if (this.model.bank_id && this.model.bank_account_no && !this.selectedAccountId) {
                    const bank = banks.find(b => b.bank_id === this.model.bank_id);
                    if (bank) {
                        const acc = (bank.accounts || []).find(a => a.account_no === this.model.bank_account_no);
                        if (acc) this.selectedAccountId = acc.account_id;
                    }
                }
            }
        },
        filteredAccounts(accounts) {
            // Restore selectedAccountId when accounts become available (edit mode)
            if (!this.selectedAccountId && this.model.bank_account_no && accounts.length) {
                const acc = accounts.find(a => a.account_no === this.model.bank_account_no);
                if (acc) this.selectedAccountId = acc.account_id;
                return;
            }
            // Auto-select if only one account for this bank
            if (!this.selectedAccountId && accounts.length === 1) {
                this.selectedAccountId = accounts[0].account_id;
                this.onAccountChange(accounts[0].account_id);
            }
        }
    },
    computed: {
        model() {
            return this.value;
        },
        nameInitial() {
            return this.requestedByName ? this.requestedByName.charAt(0).toUpperCase() : "?";
        },
        filteredAccounts() {
            if (!this.model.bank_id) return [];
            const bank = this.banks.find(b => b.bank_id === this.model.bank_id);
            return bank ? (bank.accounts || []) : [];
        },
        selectedBank() {
            if (!this.model.bank_id) return null;
            return this.banks.find(b => b.bank_id === this.model.bank_id) || null;
        },
        selectedAccount() {
            if (!this.selectedAccountId) return null;
            return this.filteredAccounts.find(a => a.account_id === this.selectedAccountId) || null;
        },
        formattedAmount() {
            const amount = parseFloat(this.model.request_amount);
            if (!this.model.request_amount || isNaN(amount) || amount <= 0) return null;
            const cur = this.currencies.find(c => c.currency_id === this.model.currency_id);
            const code = cur ? cur.currency_code : "";
            return `${code} ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
    },
    methods: {
        onBankChange() {
            this.selectedAccountId = null;
            this.model.bank_account_no = "";
            this.model.bank_account_name = "";
        },
        onAccountChange(accountId) {
            const acc = this.filteredAccounts.find(a => a.account_id === accountId);
            if (acc) {
                this.model.bank_account_no = acc.account_no;
                this.model.bank_account_name = acc.account_name;
            }
        },
        clearAccount() {
            this.selectedAccountId = null;
            this.model.bank_account_no = "";
            this.model.bank_account_name = "";
        }
    }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.slide-down-enter-active { transition: all 0.25s ease; }
.slide-down-leave-active { transition: all 0.2s ease; }
.slide-down-enter { transform: translateY(-6px); opacity: 0; }
.slide-down-leave-to { transform: translateY(-4px); opacity: 0; }
</style>
